/**
 * Aeries program-code mapping helpers — pure, no Firebase.
 *
 * Aeries tracks a student's program in a district-defined user field (U1–U8),
 * so both the column AND the codes inside it vary by district. Nothing here
 * decides anything on its own: it narrows the column down to a short list of
 * candidates and proposes plan mappings for codes that follow well-known
 * conventions. An admin confirms every one.
 *
 * The reason suggestions are never auto-applied: `R` means RSP in one district
 * and "Retained" or "Referred" in another. Applying a built-in rule silently
 * would mislabel a student's special-education status while looking like a
 * clean import.
 */

import { PROGRAM_EXCLUDE, resolveProgram } from '@/composables/scheduleImport/aeriesCsvAdapter'
import { PLAN_STATUS_INITIAL, planDisplayLabel, storedPlanValue } from '@/utils/planStatus.js'
import { PLAN_OTHER } from '@/utils/studentPlan.js'
import { PLAN_TYPE_NONE, PLAN_TYPE_TARGETS } from '@/utils/seisPlanType.js'

export { PROGRAM_EXCLUDE }

/** The only values allowed in app.studentData.plan. */
export const VALID_PLANS = ['IEP', '504']

/**
 * What an Aeries program-map rule may store. Same tokens as SEIS plan-type
 * mapping (`IEP:initial` / `504:initial`), plus Exclude. A district that flags
 * initials in a user field (Vacaville: User5 = X → IEP initial) confirms the
 * rule; `X` is never assumed.
 */
export const AERIES_PROGRAM_TARGETS = [
  ...PLAN_TYPE_TARGETS.filter((t) => t.value !== PLAN_TYPE_NONE),
  { value: PLAN_OTHER, label: 'Other', plan: PLAN_OTHER, status: '' },
  { value: PROGRAM_EXCLUDE, label: 'Exclude (skip student)', plan: PROGRAM_EXCLUDE, status: '' }
]

function aeriesTarget(value) {
  const trimmed = String(value ?? '').trim()
  return AERIES_PROGRAM_TARGETS.find((t) => t.value === trimmed) || null
}

/** True when a value is a real plan the app understands. */
export function isValidPlan(value) {
  const trimmed = String(value ?? '').trim()
  return VALID_PLANS.some((plan) => plan.toLowerCase() === trimmed.toLowerCase())
}

/** Users with role admin_504 (need a real id). */
export function school504Coordinators(caseManagers = []) {
  return (caseManagers || []).filter((user) => user?.role === 'admin_504' && user.id)
}

/** Sole 504 coordinator, or blank when there are zero or several. */
export function pickSole504CoordinatorId(caseManagers = []) {
  const coordinators = school504Coordinators(caseManagers)
  return coordinators.length === 1 ? coordinators[0].id : ''
}

/**
 * Empty-CM Initial pending student — New Evaluation / SEIS-new fillable shell.
 * Aeries 504 Accept may write the live 504 here; the IEP eval stays put.
 * Legacy referrals may lack a stored type; Initial still identifies the shell.
 */
export function isFillableEvalShellStudent({ appPlanStatus, appCaseManagerId } = {}) {
  const initial =
    String(appPlanStatus || '')
      .trim()
      .toLowerCase() === PLAN_STATUS_INITIAL
  const cm = storedPlanValue(appCaseManagerId)
  return initial && !cm
}

/** True when a mapping / export value is IEP, 504, or either initial form. */
export function isValidPlanTarget(value) {
  const trimmed = String(value ?? '').trim()
  return AERIES_PROGRAM_TARGETS.some((t) => t.value === trimmed && t.value !== PROGRAM_EXCLUDE)
}

/** Label for a stored mapping or export plan token. */
export function aeriesPlanDisplay(value) {
  if (value === PROGRAM_EXCLUDE) return 'Exclude'
  const target = aeriesTarget(value)
  if (target?.plan && target.value !== PROGRAM_EXCLUDE) {
    return planDisplayLabel(target.plan, target.status)
  }
  return String(value || '')
}

/**
 * Plan letter stored on the student from an Aeries mapping token.
 * Unmapped codes (`X`, `R`) resolve to blank — never write them as `plan`.
 */
export function aeriesExportPlanFields(exportPlan) {
  const trimmed = String(exportPlan ?? '').trim()
  const target = aeriesTarget(trimmed)
  if (target?.plan && target.value !== PROGRAM_EXCLUDE) {
    return {
      plan: target.plan,
      planStatus: target.status === PLAN_STATUS_INITIAL ? PLAN_STATUS_INITIAL : ''
    }
  }
  if (isValidPlan(trimmed)) return { plan: trimmed, planStatus: '' }
  return { plan: '', planStatus: '' }
}

/**
 * Whether the app student and the mapped Aeries code agree enough that
 * the importer should leave plan alone.
 *
 * Same plan letter + export initial → agree (never demote a live plan).
 * A missing letter needs an explicit fill, even when Initial is already set.
 * Initial alone does not identify an IEP referral versus a 504 referral.
 * Same letter + export established + app initial → disagree (Aeries now
 * says the plan is in force; Accept clears `planStatus`).
 * Blank plan + export in force → disagree. A stored letter that differs
 * from Aeries → disagree.
 */
export function aeriesPlansAgree(appPlan, appPlanStatus, exportPlan) {
  const exportTrim = String(exportPlan || '').trim()
  if (!exportTrim) return true

  const { plan: exportLetter, planStatus: exportStatus } = aeriesExportPlanFields(exportTrim)
  if (!exportLetter) return false

  const appLetter = storedPlanValue(appPlan)
  const appIsInitial =
    String(appPlanStatus || '')
      .trim()
      .toLowerCase() === PLAN_STATUS_INITIAL

  if (!appLetter) return false
  if (appLetter.toLowerCase() !== exportLetter.toLowerCase()) return false

  if (exportStatus === PLAN_STATUS_INITIAL) return true
  return !appIsInitial
}

/**
 * What the plan-conflict button will write, in words the importer can show
 * before anything is saved.
 *
 * `clearsInitial` is the dangerous case: the app still has Initial (no plan
 * document yet) and the Aeries token is a plan in force. Accepting removes
 * Initial. Same plan letter is still a real change.
 *
 * @returns {{ plan: string, planStatus: string, fromLabel: string, toLabel: string,
 *   clearsInitial: boolean, programRaw: string, buttonLabel: string } | null}
 */
export function describeAeriesPlanAccept({
  appPlan,
  appPlanStatus,
  exportPlan,
  programRaw = ''
} = {}) {
  const fields = aeriesExportPlanFields(exportPlan)
  if (!fields.plan) return null
  const appIsInitial =
    String(appPlanStatus || '')
      .trim()
      .toLowerCase() === PLAN_STATUS_INITIAL
  const clearsInitial = appIsInitial && fields.planStatus !== PLAN_STATUS_INITIAL
  const appLetter = storedPlanValue(appPlan)
  const missingInitialType = appIsInitial && !appLetter
  const fromLabel = missingInitialType
    ? 'Initial — plan type not stored'
    : planDisplayLabel(appLetter, appPlanStatus) || 'No plan'
  const toLabel = planDisplayLabel(fields.plan, fields.planStatus)
  return {
    plan: fields.plan,
    planStatus: fields.planStatus,
    fromLabel,
    toLabel,
    clearsInitial,
    programRaw: String(programRaw || '').trim(),
    buttonLabel: clearsInitial
      ? `Set ${toLabel} in force`
      : missingInitialType
        ? `Fill ${fields.plan} — keep Initial`
        : 'Use Aeries'
  }
}

/**
 * What Accept / Set plan may write. Refuses to stamp `planStatus: initial`
 * onto a student who already has a plan in force.
 *
 * On a fillable eval shell (Initial pending, empty plan CM) + established 504:
 * write 504, clear planStatus, assign the school's 504 coordinator when known.
 * Never writes evalCaseManagerId — the IEP eval stays assigned as-is.
 *
 * @returns {{ ok: true, studentData: object, shell504Accept?: boolean,
 *   needsCoordinatorPick?: boolean, noCoordinator?: boolean } | { ok: false, error: string }}
 */
export function buildAeriesPlanAcceptWrite({
  appPlan,
  appPlanStatus,
  exportValue,
  appCaseManagerId = '',
  coordinatorId = '',
  caseManagers = []
} = {}) {
  const { plan, planStatus } = aeriesExportPlanFields(exportValue)
  if (!plan) {
    return {
      ok: false,
      error:
        `"${exportValue}" is not a plan. Map this Aeries code to IEP, 504, Other, or an initial first, ` +
        'and every student with that code is fixed at once.'
    }
  }

  const appLetter = storedPlanValue(appPlan)
  const appIsInitial =
    String(appPlanStatus || '')
      .trim()
      .toLowerCase() === PLAN_STATUS_INITIAL
  const appEstablished = Boolean(appLetter) && !appIsInitial

  if (planStatus === PLAN_STATUS_INITIAL && appEstablished) {
    return {
      ok: false,
      error:
        'Cannot apply an initial mapping to a student who already has a plan in force. ' +
        'Leave their plan as-is, or set IEP / 504 if the live plan is wrong.'
    }
  }

  // The letter identifies the referral; Initial keeps it out of active-plan
  // counts. Lists, access rules and the calendar still depend on the letter.
  const studentData = { plan, planStatus }

  // Other plans are case-managed by a 504 coordinator (same rule as the
  // student form). Keep a coordinator already assigned; otherwise assign the
  // sole coordinator, or require a pick when there are several.
  if (plan === PLAN_OTHER) {
    const coordinators = school504Coordinators(caseManagers)
    if (!coordinators.length) {
      return {
        ok: false,
        error:
          'Other plans need a 504 coordinator as case manager. Add that role (Admin → Users) first.'
      }
    }
    const current = String(appCaseManagerId || '').trim()
    if (coordinators.some((c) => c.id === current)) {
      return { ok: true, studentData }
    }
    const picked = String(coordinatorId || '').trim()
    const validPick = picked && coordinators.some((c) => c.id === picked) ? picked : ''
    const cm = validPick || (coordinators.length === 1 ? coordinators[0].id : '')
    if (cm) {
      studentData.caseManagerId = cm
      return { ok: true, studentData }
    }
    return {
      ok: false,
      error: 'Other plans are case-managed by a 504 coordinator. Pick one.',
      needsCoordinatorPick: true
    }
  }

  const shell = isFillableEvalShellStudent({
    appPlan,
    appPlanStatus,
    appCaseManagerId
  })
  const accepting504InForce = plan === '504' && planStatus !== PLAN_STATUS_INITIAL

  if (!shell || !accepting504InForce) {
    return { ok: true, studentData }
  }

  const coordinators = school504Coordinators(caseManagers)
  // An explicit pick must be one of the school's admin_504 users — a stale or
  // wrong id from a caller must not become the plan CM.
  const picked = String(coordinatorId || '').trim()
  const validPick = picked && coordinators.some((c) => c.id === picked) ? picked : ''
  const cm = validPick || pickSole504CoordinatorId(caseManagers)
  if (cm) {
    studentData.caseManagerId = cm
    return {
      ok: true,
      studentData,
      shell504Accept: true,
      needsCoordinatorPick: false,
      noCoordinator: false
    }
  }

  if (coordinators.length > 1) {
    return {
      ok: false,
      error:
        'This student is an initial-eval shell. Pick a 504 coordinator as the plan case manager — the IEP evaluation stays assigned to its eval CM.',
      shell504Accept: true,
      needsCoordinatorPick: true,
      noCoordinator: false
    }
  }

  return {
    ok: true,
    studentData,
    shell504Accept: true,
    needsCoordinatorPick: false,
    noCoordinator: true
  }
}

/** True when the mapped Aeries code is a 504 (in force or initial). */
export function isAeries504Plan(exportPlan) {
  return aeriesExportPlanFields(exportPlan).plan === '504'
}

/**
 * Case manager to pre-select on Add-to-App. 504 / 504-initial students go to
 * the school's 504 coordinator when there is exactly one. IEP rows stay blank
 * — the importer must pick. Multiple 504 coordinators also stay blank so we
 * do not guess.
 */
export function defaultCaseManagerIdForAdd({ exportPlan, caseManagers = [] } = {}) {
  const { plan } = aeriesExportPlanFields(exportPlan)
  if (plan !== '504' && plan !== PLAN_OTHER) return ''
  const coordinators = caseManagers.filter((u) => u?.role === 'admin_504' && u.id)
  return coordinators.length === 1 ? coordinators[0].id : ''
}

/**
 * Conventional Aeries program codes. Used only to pre-fill a suggestion the
 * admin confirms — see the file header for why these never apply themselves.
 */
export const PROGRAM_CODE_SUGGESTIONS = [
  { test: /^(504|4|s4)$/i, plan: '504', label: 'Section 504 plan' },
  { test: /^(r|rsp|res|resource)$/i, plan: 'IEP', label: 'RSP — Resource Specialist Program' },
  { test: /^(s|sdc|special\s*day)$/i, plan: 'IEP', label: 'SDC — Special Day Class' },
  { test: /^(i|iep|sped|sp\s*ed|se)$/i, plan: 'IEP', label: 'IEP' },
  { test: /^(dhh|hh|vi|oi)$/i, plan: 'IEP', label: 'Low-incidence program' },
  {
    test: /^(n|no|none|reg|regular|gen|general|ge)$/i,
    plan: PROGRAM_EXCLUDE,
    label: 'Not in a special-education program'
  }
]

/**
 * Suggested plan for a raw Aeries code, or null when no convention matches.
 * @param {string} code
 * @returns {{ plan: string, label: string }|null}
 */
export function suggestPlanForCode(code) {
  const trimmed = String(code ?? '').trim()
  if (!trimmed) return null
  const hit = PROGRAM_CODE_SUGGESTIONS.find((entry) => entry.test.test(trimmed))
  return hit ? { plan: hit.plan, label: hit.label } : null
}

/** Case-insensitive lookup into a saved programFieldMapping.mappings object. */
export function lookupMapping(code, mappings = {}) {
  const trimmed = String(code ?? '')
    .trim()
    .toLowerCase()
  if (!trimmed) return ''
  for (const [key, plan] of Object.entries(mappings || {})) {
    if (String(key).trim().toLowerCase() === trimmed) return plan
  }
  return ''
}

/**
 * Collapse per-student program mismatches into one row per Aeries code.
 *
 * A caseload-wide mismatch list is usually a handful of unmapped codes repeated
 * hundreds of times, so the decision belongs at the code level. Rows split into
 * two kinds:
 *
 * - `unmapped`: Aeries gave a code the app can't read as a plan. One mapping
 *   rule fixes every student carrying it.
 * - `conflict`: Aeries resolved to a real plan that disagrees with the app.
 *   Genuinely per-student; no rule will help.
 *
 * @param {Array<{ssid:string, name:string, appPlan:string, exportPlan:string, programRaw:string}>} mismatches
 * @param {object} [mappings] - saved programFieldMapping.mappings
 * @returns {{ unmapped: Array<Object>, conflicts: Array<Object>, unmappedStudentCount: number }}
 */
export function groupProgramMismatches(mismatches = [], mappings = {}) {
  const groups = new Map()

  for (const row of mismatches) {
    const code = String(row?.programRaw || row?.exportPlan || '').trim()
    if (!code) continue
    const key = code.toLowerCase()
    if (!groups.has(key)) {
      groups.set(key, {
        code,
        students: [],
        mappedPlan: lookupMapping(code, mappings),
        suggestion: suggestPlanForCode(code)
      })
    }
    groups.get(key).students.push(row)
  }

  const unmapped = []
  const conflicts = []
  for (const group of groups.values()) {
    const entry = { ...group, count: group.students.length }
    // A code the app resolved to a real plan is a per-student disagreement;
    // anything else is a vocabulary gap one rule can close.
    if (isValidPlanTarget(entry.students[0]?.exportPlan)) conflicts.push(entry)
    else unmapped.push(entry)
  }

  const byCount = (a, b) => b.count - a.count || a.code.localeCompare(b.code)
  unmapped.sort(byCount)
  conflicts.sort(byCount)

  return {
    unmapped,
    conflicts,
    unmappedStudentCount: unmapped.reduce((sum, g) => sum + g.count, 0)
  }
}

/**
 * Re-resolve every student's plan against an updated mapping.
 *
 * Lets a newly added rule take effect without re-parsing the CSV or re-pulling
 * the API — both paths keep `programRaw`, so the plan can be recomputed in
 * place. Students whose code now maps to Exclude move to `excludedStudents`.
 *
 * @param {{students?:object, excludedStudents?:object}} normalized
 * @param {object|null} programMapping
 * @returns {object} a new normalized object
 */
export function reapplyProgramMapping(normalized = {}, programMapping = null) {
  const students = {}
  const excludedStudents = { ...(normalized.excludedStudents || {}) }

  for (const [id, data] of Object.entries(normalized.students || {})) {
    const plan = resolveProgram(data.programRaw, programMapping)
    if (plan === PROGRAM_EXCLUDE) {
      excludedStudents[id] = {
        firstName: data.firstName,
        lastName: data.lastName,
        grade: data.grade,
        programRaw: data.programRaw
      }
      continue
    }
    students[id] = { ...data, plan }
  }

  return { ...normalized, students, excludedStudents }
}

/** Headers that are already claimed by schedule/identity detection. */
function usedHeaders(columnMapping = {}) {
  return new Set(
    Object.values(columnMapping)
      .filter((v) => typeof v === 'string' && v)
      .map((v) => v.trim().toLowerCase())
  )
}

/** Header names districts commonly use for the program field. */
const PROGRAM_HEADER_PATTERNS = [
  /^(stu\.)?u(ser)?\s*(code)?\s*[1-8]$/i,
  /sp\s*ed/i,
  /special\s*ed/i,
  /program/i,
  /plan\s*type/i,
  /^se$/i
]

const looksLikeProgramHeader = (header) =>
  PROGRAM_HEADER_PATTERNS.some((re) => re.test(String(header || '').trim()))

/**
 * Columns that could hold the program code, each with the distinct values found
 * in the file.
 *
 * Header names alone are not enough to pick between U1 and U5 — but seeing
 * `Stu.U3 → R, S, O, F, 4` next to `Stu.U1 → Y, N` makes it obvious. So this
 * ranks by name convention and returns the values for the admin to recognize.
 *
 * @param {string[]} headers
 * @param {object[]} rows
 * @param {object} [opts]
 * @param {object} [opts.columnMapping] - already-detected schedule columns to skip
 * @param {number} [opts.maxDistinct] - above this a column is free text, not a code
 * @param {number} [opts.sampleRows] - rows to scan
 * @returns {Array<{header:string, distinctValues:string[], distinctCount:number, likely:boolean}>}
 */
export function detectProgramColumnCandidates(
  headers = [],
  rows = [],
  { columnMapping = {}, maxDistinct = 12, sampleRows = 500 } = {}
) {
  const skip = usedHeaders(columnMapping)
  const sample = rows.slice(0, sampleRows)
  const candidates = []

  for (const header of headers) {
    const key = String(header || '').trim()
    if (!key || skip.has(key.toLowerCase())) continue

    const values = new Set()
    let tooMany = false
    for (const row of sample) {
      const value = String(row?.[header] ?? '').trim()
      if (!value) continue
      // Codes are short; a long value means this is a name or description.
      if (value.length > 12) {
        tooMany = true
        break
      }
      values.add(value)
      if (values.size > maxDistinct) {
        tooMany = true
        break
      }
    }
    if (tooMany || !values.size) continue

    candidates.push({
      header: key,
      distinctValues: [...values].sort(),
      distinctCount: values.size,
      likely: looksLikeProgramHeader(key)
    })
  }

  // Name matches first, then the tightest vocabularies — a 4-value column is a
  // far better program candidate than a 12-value one.
  candidates.sort(
    (a, b) => Number(b.likely) - Number(a.likely) || a.distinctCount - b.distinctCount
  )
  return candidates
}

/**
 * Whether the configured program column actually resolved against this file.
 *
 * Distinguishes the three states that otherwise all end in a blank plan: the
 * configured column is absent, nothing was configured and detection found
 * nothing, or the district simply does not export a program column.
 *
 * @param {object} args
 * @param {object|null} args.programMapping
 * @param {string[]} args.headers
 * @param {string} [args.detectedColumn] - auto-detected userField, if any
 * @returns {{ status: 'ok'|'configured-missing'|'unconfigured'|'none-found',
 *             column: string, message: string }}
 */
export function resolveProgramColumnStatus({
  programMapping = null,
  headers = [],
  detectedColumn = ''
} = {}) {
  const lower = headers.map((h) =>
    String(h || '')
      .trim()
      .toLowerCase()
  )
  const configured = String(programMapping?.sourceColumn || '').trim()

  if (configured) {
    const index = lower.indexOf(configured.toLowerCase())
    if (index >= 0) {
      return { status: 'ok', column: headers[index], message: '' }
    }
    return {
      status: 'configured-missing',
      column: '',
      message:
        `This school is set to read programs from "${configured}", but that column is not in ` +
        `the uploaded file. Pick the column that holds the program code, or clear the setting.`
    }
  }

  if (detectedColumn) {
    return { status: 'ok', column: detectedColumn, message: '' }
  }

  if (programMapping?.enabled) {
    return {
      status: 'unconfigured',
      column: '',
      message:
        'Program mapping is on but no source column is set, and none was auto-detected. ' +
        'Pick the column that holds the program code.'
    }
  }

  return {
    status: 'none-found',
    column: '',
    message:
      'No program column was found in this file, so plan types will be left as they are. ' +
      'Pick a column if your export includes one.'
  }
}
